import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FileTransfersService, IOcbEnvironmentAccess, IOcbFileTransferLog, OcbFileTransferLogTypes, UserService } from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import notify from 'devextreme/ui/notify';
import { Observable, forkJoin } from 'rxjs';
import { finalize, mergeMap, tap } from 'rxjs/operators';
import { FileTransferLogComponent } from '../../shared';

const now = new Date();
const initialDates: [Date, Date] = [new Date(now), new Date(now)];

@Component({
    selector: 'app-home-history-file-transfers',
    templateUrl: './home-history-file-transfers.component.html',
    styleUrls: ['./home-history-file-transfers.component.scss'],
})
export class HomeHistoryFileTransfersComponent implements AfterViewInit {

    @ViewChild('logs')
    public logsGrid: DxDataGridComponent;
    @ViewChild('fileTransferLogPopup')
    public fileTransferLogPopup: FileTransferLogComponent;

    // Main Data-Grid
    public dataSource: IOcbFileTransferLog[] = null;

    // Date selectors
    public maxDate: Date = now;
    public selectedDates: [Date, Date] = initialDates;
    public environments: IOcbEnvironmentAccess[] = [];
    public selectedEnvironment: IOcbEnvironmentAccess;

    public pagingInfos = {
        enabled: true,
        pageIndex: 0,
        pageSize: 10,
    };

    private logsTypesLabels: any[];

    constructor(
        private route: ActivatedRoute,
        private userService: UserService,
        private fileTransfersService: FileTransfersService,
        private translateService: TranslateService,
        private router: Router) {
        this.displayDateAsString = this.displayDateAsString.bind(this);
        this.displayType = this.displayType.bind(this);
    }

    public ngAfterViewInit() {
        this.userService.user.subscribe(admin => {
            this.environments = admin.environments.filter(env => env.canAccessLogs);
            if (this.environments.length > 0) {
                this.selectedEnvironment = this.environments.find(env => env.environmentId === this.userService.currentEnvironment);
            }
        });
        this.route.params.subscribe(_ => {
            this.translateService.get('fileTransferLogTypes').subscribe(logTypes => {
                this.logsTypesLabels = logTypes;
                this.loadDataSource();
            });
        });
    }

    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data' && this.selectedEnvironment) {
            forkJoin([
                this.translateService.get('_commons.seeLogDetails'),
                this.translateService.get('_commons.seeFlowDetails')])
                .subscribe(messages => {
                    e.items = [
                        { text: messages[0], icon: 'clock', onItemClick: (ev) => this.showDetail({ data: e.row.data }) },
                        { text: messages[1], icon: 'description', beginGroup: true, onItemClick: (ev) => this.router.navigateByUrl(`/${this.selectedEnvironment?.environmentId}/file-transfers/${e.row.data.fileTransferId}`) }
                    ];
                });
        }
    }

    private getSelectedEndDate() { return this.selectedDates[1].toJSON().slice(0, 10); }

    private getSelectedStartDate() { return this.selectedDates[0].toJSON().slice(0, 10); }

    private getCurrentLocale(): string {
        let currentLocale = 'en-US';
        switch (this.userService.language) {
            case 'en': currentLocale = 'en-US'; break;
            case 'fr': currentLocale = 'fr-FR'; break;
        }
        return currentLocale;
    }

    public displayDate(rowData): Date {
        const date = new Date(rowData.transferDate + 'Z');
        return date;
    }

    public displayDateAsString(rowData): string {
        const locale = this.getCurrentLocale();
        return this.displayDate(rowData).toLocaleString(locale);
    }

    public displayType(rowData) {
        return this.logsTypesLabels[OcbFileTransferLogTypes[rowData.logType]];
    }

    public showDetail(e) {
        if (!e?.data) return;
        this.fileTransferLogPopup.log = e.data;
        this.fileTransferLogPopup.visible = true;
    }

    public dateChanged(): void {
        this.loadDataSource();
    }

    public formatSourceFileSize(rowData): string {
        if (rowData.sourceFileSize == null) return null;
        return !rowData.sourceFileSize ? '0.00' : rowData.sourceFileSize.toFixed(2);
    }

    public loadDataSource() {
        if (this.selectedEnvironment) {
            this.showLoading$('_commons.loading')
                .pipe(
                    mergeMap(_ => this.fileTransfersService.getAllLogs(this.getSelectedStartDate(), this.getSelectedEndDate(), this.selectedEnvironment.environmentId)),
                    finalize(() => this.logsGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.dataSource = res,
                    error: (err) => this.handleError(err)
                });
        }
    }

    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.logsGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }
}
